/**
 * Shared viewport-progress helpers used by both the image/video scroll
 * animations and the text-animation scrub controller. Keeping these in one
 * place ensures both systems map "where is the element relative to the
 * viewport" to "0..1 progress" the exact same way.
 *
 * (Originally lived inside scroll_animations.js — extracted unchanged so the
 * text scrub feature can reuse it.)
 */

export const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v)

const getDirectLayContentChild = (root) => {
	if (!root || !root.children) return null
	for (let i = 0; i < root.children.length; i++) {
		const child = root.children[i]
		if (child instanceof HTMLElement && child.classList.contains('lay-content')) return child
	}
	return null
}

const canElementScrollVertically = (el) => {
	if (!(el instanceof HTMLElement)) return false
	return el.scrollHeight > el.clientHeight
}

const getFixedOverlayContextForElement = (el) => {
	if (!el || typeof el.closest !== 'function') return null

	const pageOverlay = el.closest('.lay-overlay')
	if (pageOverlay) {
		const directLayContent = getDirectLayContentChild(pageOverlay)
		const scrollRoot = canElementScrollVertically(directLayContent) ? directLayContent : pageOverlay
		if (scrollRoot === pageOverlay || scrollRoot.contains(el)) {
			return {
				coordinateRoot: pageOverlay,
				scrollRoot,
			}
		}
	}

	const projectOverlay = el.closest('#lay-project-overlay')
	if (projectOverlay) {
		return {
			coordinateRoot: projectOverlay,
			scrollRoot: projectOverlay,
		}
	}

	return null
}

/**
 * Snapshot of the viewport metrics used by getProgress. Reading
 * `document.body.scrollHeight` / `documentElement.scrollHeight` triggers
 * layout, so callers that update many elements in a single rAF tick should
 * compute this once per tick and pass it to every getProgress call instead
 * of letting each call re-read the DOM.
 */
export const getScrollContainerForElement = (el) => {
	const overlayContext = getFixedOverlayContextForElement(el)
	if (overlayContext) return overlayContext.scrollRoot

	let p = el ? el.parentElement : null
	while (p instanceof HTMLElement) {
		const style = window.getComputedStyle(p)
		const oy = style.overflowY
		if ((oy === 'auto' || oy === 'overlay' || oy === 'scroll') && p.scrollHeight > p.clientHeight) {
			return p
		}
		p = p.parentElement
	}
	return window
}

export const getIntersectionObserverRootForElement = (el) => {
	const scrollContainer = getScrollContainerForElement(el)
	if (
		!scrollContainer ||
		scrollContainer === window ||
		scrollContainer === document.body ||
		scrollContainer === document.documentElement
	) {
		return null
	}
	return scrollContainer
}

export const getViewportContext = (scrollContainer) => {
	const container = scrollContainer || window
	if (container !== window) {
		const vh = container.clientHeight || window.innerHeight || document.documentElement.clientHeight || 0
		const scrollTop = container.scrollTop || 0
		const scrollHeight = container.scrollHeight || 0
		return { vh, scrollTop, scrollHeight }
	}

	const vh = window.innerHeight || document.documentElement.clientHeight || 0
	const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0
	const scrollHeight = Math.max(
		document.documentElement.scrollHeight || 0,
		document.body ? document.body.scrollHeight : 0,
	)
	return { vh, scrollTop, scrollHeight }
}

const getTransformTranslate = (el) => {
	if (!el || !el.style) return { x: 0, y: 0 }
	const transform = el.style.transform || el.style.webkitTransform || ''
	if (!transform || transform === 'none') return { x: 0, y: 0 }

	if (transform.indexOf('matrix3d(') === 0) {
		const values = transform.substring(9, transform.length - 1).split(',')
		return {
			x: parseFloat(values[12]) || 0,
			y: parseFloat(values[13]) || 0,
		}
	}

	if (transform.indexOf('matrix(') === 0) {
		const values = transform.substring(7, transform.length - 1).split(',')
		return {
			x: parseFloat(values[4]) || 0,
			y: parseFloat(values[5]) || 0,
		}
	}

	if (transform.indexOf('translate3d(') === 0 || transform.indexOf('translate(') === 0) {
		const start = transform.indexOf('(') + 1
		const values = transform.substring(start, transform.indexOf(')')).split(',')
		return {
			x: parseTranslateValue(values[0], 'x', el),
			y: parseTranslateValue(values[1], 'y', el),
		}
	}

	return { x: 0, y: 0 }
}

const parseTranslateValue = (value, axis, el) => {
	if (!value) return 0
	const str = String(value).trim()
	const n = parseFloat(str)
	if (!isFinite(n)) return 0
	if (str.indexOf('vw') > -1) return (window.innerWidth / 100) * n
	if (str.indexOf('vh') > -1) return (window.innerHeight / 100) * n
	if (str.indexOf('%') > -1) {
		return ((axis === 'x' ? el.offsetWidth : el.offsetHeight) / 100) * n
	}
	return n
}

const getOffsetWithinRoot = (el, root) => {
	let top = 0
	let left = 0
	let cur = el
	while (cur instanceof HTMLElement) {
		top += cur.offsetTop
		left += cur.offsetLeft
		if (cur === root) {
			return { top, left }
		}
		const next = cur.offsetParent
		cur = next instanceof HTMLElement ? next : null
	}
	return null
}

const getNaturalViewportRectInFixedOverlay = (el, overlayContext) => {
	const offset = getOffsetWithinRoot(el, overlayContext.coordinateRoot)
	if (!offset) return null

	let scrollTopAcc = 0
	let scrollLeftAcc = 0
	let transformTopAcc = 0
	let transformLeftAcc = 0
	let s = el.parentElement
	while (s instanceof HTMLElement && s !== overlayContext.coordinateRoot) {
		scrollTopAcc += s.scrollTop
		scrollLeftAcc += s.scrollLeft
		const transformTranslate = getTransformTranslate(s)
		transformTopAcc += transformTranslate.y
		transformLeftAcc += transformTranslate.x
		s = s.parentElement
	}

	if (overlayContext.scrollRoot === overlayContext.coordinateRoot) {
		scrollTopAcc += overlayContext.scrollRoot.scrollTop
		scrollLeftAcc += overlayContext.scrollRoot.scrollLeft
	}

	const t = offset.top - scrollTopAcc + transformTopAcc
	const l = offset.left - scrollLeftAcc + transformLeftAcc
	const w = el.offsetWidth
	const h = el.offsetHeight
	return { top: t, left: l, right: l + w, bottom: t + h, width: w, height: h }
}

/**
 * Compute the linear scroll progress for an element. 0 = element top has just
 * entered the viewport from the bottom (or further below — clamped), 1 =
 * element top has crossed the "endAtTop" threshold (or higher). Easing is
 * applied separately at the call-site.
 *
 * Edge case: the LAST element on a page may never reach the configured
 * endAtTop threshold because the document can't scroll further down. In that
 * case we rescale the span to whatever scroll runway is actually available so
 * the animation still completes by the time the user hits the bottom of the
 * page — instead of being stuck at e.g. 60% mid-slide.
 *
 * @param {{top: number, bottom: number}} rect natural viewport rect (see getNaturalViewportRect)
 * @param {number} endAtTopPct e.g. 90 → animation completes when element top is at 10% from viewport top
 * @param {boolean} endAtFullExit override: progress=1 when element bottom crosses viewport top
 * @param {number} [startAtBottomPct=0] % of viewport the element top has to travel UP from the viewport-bottom edge before progress starts climbing. 0 = animation begins as soon as element top enters the viewport. 20 = wait until element top has traveled 20% of viewport height (so element is "20% into the viewport") before progress > 0.
 * @returns {number} 0..1
 */
export const getProgress = (rect, endAtTopPct, endAtFullExit, startAtBottomPct, ctx) => {
	const vh = ctx ? ctx.vh : window.innerHeight || document.documentElement.clientHeight
	if (vh <= 0) return 0

	const startPct = isFinite(startAtBottomPct) && startAtBottomPct > 0 ? startAtBottomPct : 0
	const startY = vh * (1 - startPct / 100)

	let endY
	if (endAtFullExit) {
		const h = rect.bottom - rect.top
		endY = -h
	} else {
		endY = vh * (1 - endAtTopPct / 100)
	}

	let span = startY - endY
	if (span <= 0) return rect.top <= endY ? 1 : 0

	const scrollTop = ctx ? ctx.scrollTop : window.pageYOffset || document.documentElement.scrollTop || 0
	const scrollHeight = ctx
		? ctx.scrollHeight
		: Math.max(
				document.documentElement.scrollHeight || 0,
				document.body ? document.body.scrollHeight : 0,
		  )
	const remainingScroll = Math.max(0, scrollHeight - vh - scrollTop)
	const minReachableTop = rect.top - remainingScroll
	if (minReachableTop > endY) {
		const truncatedSpan = startY - minReachableTop
		if (truncatedSpan <= 0) return rect.top <= minReachableTop ? 1 : 0
		span = truncatedSpan
	}

	const traveled = startY - rect.top
	return clamp01(traveled / span)
}

/**
 * Compute the element's NATURAL viewport rect — i.e. where it would be in
 * the viewport if no transforms were applied. We can't use getBoundingClientRect
 * because skew/scale/translate distort the reported rect; with skew enabled,
 * rect.top depends on the current skew angle, the angle depends on progress,
 * progress depends on rect.top — a feedback loop that visibly flickers when
 * scroll is idle.
 *
 * offsetTop / offsetLeft walk the layout tree without the element's own
 * transform contributions. Subtracting scrollTop along ancestors converts
 * document coordinates to viewport coordinates — also makes this work inside
 * `#lay-project-overlay` and page overlays which scroll internally rather
 * than via window. Ancestor translate transforms are added back in so parent
 * systems like parallax, offsets, and masonry positioning are reflected
 * without feeding the scroll-animation target's own transform back into the
 * progress calculation.
 */
export const getNaturalViewportRect = (el) => {
	const overlayContext = getFixedOverlayContextForElement(el)
	if (overlayContext) {
		const overlayRect = getNaturalViewportRectInFixedOverlay(el, overlayContext)
		if (overlayRect) return overlayRect
	}

	let top = 0
	let left = 0
	let cur = el
	while (cur instanceof HTMLElement) {
		top += cur.offsetTop
		left += cur.offsetLeft
		const next = cur.offsetParent
		cur = next instanceof HTMLElement ? next : null
	}
	let scrollTopAcc = 0
	let scrollLeftAcc = 0
	let transformTopAcc = 0
	let transformLeftAcc = 0
	let s = el.parentElement
	while (s instanceof HTMLElement) {
		scrollTopAcc += s.scrollTop
		scrollLeftAcc += s.scrollLeft
		const transformTranslate = getTransformTranslate(s)
		transformTopAcc += transformTranslate.y
		transformLeftAcc += transformTranslate.x
		s = s.parentElement
	}
	const t = top - scrollTopAcc + transformTopAcc
	const l = left - scrollLeftAcc + transformLeftAcc
	const w = el.offsetWidth
	const h = el.offsetHeight
	return { top: t, left: l, right: l + w, bottom: t + h, width: w, height: h }
}
